import { useEffect, useRef } from "react";
import IntroBlock, { IntroData } from "../additional/intro-block";
import SystemInfoBlock from "../additional/system-info-block";
import WarningBanner from "../additional/warning-banner";
import MessageBubble from "./message-bubble";
import ConnectionError from "../additional/connection-error";

type Message = {
  id: string;
  type: "user" | "assistant" | "refusal" | "pending" | "system";
  text?: string;
};

type ConversationAreaProps = {
  messages: Message[];
  showIntro?: boolean;
  warningText?: string;
  warningVariant?: "warning" | "hard-stop";
  connectionError?: boolean;
  introData?: IntroData;
  onQuestionClick?: (question: string) => void;
  onRetryConnection?: () => void;
};

const ConversationArea = ({
  messages,
  showIntro = false,
  connectionError = false,
  warningText,
  warningVariant,
  introData,
  onQuestionClick,
  onRetryConnection
}: ConversationAreaProps) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const intro = introData || {title: '', description: '', questionsTitle: '', questions: []};

  return (
    <div
      className="flex-1 overflow-y-auto w-full [mask-image:linear-gradient(to_bottom,transparent,black_24px,black)] [-webkit-mask-image:linear-gradient(to_bottom,transparent,black_24px,black)]"
      data-name="conversation-area"
    >
      <div className="content-stretch flex flex-col gap-[15px] px-[20px] py-[20px] relative w-full">
        {showIntro && messages.length === 0 ? (
          <IntroBlock
            onQuestionClick={onQuestionClick}
            {...intro}
          />
        ) : (
          <>
            {messages.map((message) => {
              if (message.type === "system") {
                return (
                  <SystemInfoBlock key={message.id} text={message.text || ""} />
                );
              }
              return (
                <MessageBubble
                  key={message.id}
                  type={message.type}
                  text={message.text}
                />
              );
            })}
          </>
        )}

        {warningText && messages.length > 0 && (
          <WarningBanner text={warningText} variant={warningVariant} />
        )}

        {connectionError && (
          <ConnectionError onRetry={onRetryConnection} />
        )}

        <div ref={bottomRef} />
      </div>
    </div>
  );
};

export default ConversationArea;
export type { Message };
